import { useCallback, useMemo } from "react"

import { useDispatch, useSelector } from "react-redux"

import { getCompareParams } from "../../../Components/Filters/CompareSelect/presets"
import { getDates } from "../../../Utils/utils"
import { selectAnalyticsFilters } from "../redux/analytics.slice"
import { SECTION_THUNKS } from "../redux/analytics.thunks"

// The shared filters, spelled once as the three scopes a request can be read at, with a fetcher
// per scope. A section asks through the narrowest scope it needs, so its fetcher — and with it the
// section's effect — changes only when that scope does: flipping Compare refetches the blocks that
// show a comparison and leaves the rest alone.
//   getQueue   — case type only (the queues are "right now", not a window)
//   getBase    — + the calendar window
//   getCompare — + the comparison
// Each takes (key, params): the store key the answer lands in, and the section's own params.
export const useAnalyticsScope = () => {
  const dispatch = useDispatch()
  const { period, compare, dispute_type: types } = useSelector(selectAnalyticsFilters)
  const businessAccountId = useSelector((state) => state.auth.userInfo?.current_business_account_id)

  const disputeType = types?.length ? types.join(",") : null

  // Not sent — the backend reads the account off the user — but a switch is a different set of
  // data, so every scope changes with it and every section refetches.
  const queueScope = useMemo(
    () => (disputeType ? { dispute_type: disputeType } : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disputeType, businessAccountId]
  )

  const baseScope = useMemo(() => {
    const { start_date, end_date } = getDates(period)
    return start_date && end_date ? { ...queueScope, start_date, end_date } : queueScope
  }, [queueScope, period])

  const compareScope = useMemo(
    () => ({ ...baseScope, ...getCompareParams(compare, getDates(period)) }),
    [baseScope, compare, period]
  )

  const getQueue = useCallback(
    (key, params) => dispatch(SECTION_THUNKS[key]({ key, params: { ...queueScope, ...params } })),
    [dispatch, queueScope]
  )

  const getBase = useCallback(
    (key, params) => dispatch(SECTION_THUNKS[key]({ key, params: { ...baseScope, ...params } })),
    [dispatch, baseScope]
  )

  const getCompare = useCallback(
    (key, params) => dispatch(SECTION_THUNKS[key]({ key, params: { ...compareScope, ...params } })),
    [dispatch, compareScope]
  )

  return { getQueue, getBase, getCompare }
}
